import React, { useState } from 'react'
import { Col, Row, Form, Button, Spinner } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { validation, validationText } from '../utils/app-constants'
import ThemeInput from '../Components/input/ThemeInput'
import ThemeTextArea from '../Components/input/ThemeTextArea'
import { useSendPushMutation, useSendPushToUserMutation, useGetAllUsersQuery } from '../store/apis/userApi'
import { errorMsg, successMsg } from '../constants/msgs'
import Select from 'react-select'

const SendPushNotifications = () => {
    const { control, handleSubmit, reset, formState: { errors } } = useForm({ mode: 'onChange' })
    const [sendMode, setSendMode] = useState('all') // 'all' or 'user'
    const [selectedUser, setSelectedUser] = useState(null)

    const [sendPush, { isLoading: isSendingAll }] = useSendPushMutation()
    const [sendPushToUser, { isLoading: isSendingToUser }] = useSendPushToUserMutation()
    const { data: usersData, isLoading: isLoadingUsers } = useGetAllUsersQuery()

    const isLoading = isSendingAll || isSendingToUser

    // Format users for react-select
    const userOptions = usersData?.data?.map(user => ({
        value: user.slug,
        label: `${user.full_name} (${user.email})`
    })) || []

    const onSubmit = async (formData) => {
        try {
            let response

            if (sendMode === 'all') {
                response = await sendPush(formData)
            } else {
                if (!selectedUser) {
                    errorMsg('Please select a user')
                    return
                }
                response = await sendPushToUser({
                    slug: selectedUser.value,
                    body: formData
                })
            }

            if (response.data) {
                successMsg(response.data.message)
                reset()
                setSelectedUser(null)
            } else {
                errorMsg(response.error?.data?.message || 'Failed to send notification')
            }
        } catch (error) {
            errorMsg(error.message || 'Something went wrong')
        }
    }

    return (
        <div className='pages dashboard-page'>
            <Row>
                <Col xs={12} className='mb-4'>
                    <h2 className='text-black fw-800'>Notifications</h2>
                </Col>

                <Col xs={12} md={6} lg={6} className='mb-3'>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        {/* Send Mode Toggle */}
                        <div className='mb-4'>
                            <label className='form-label fw-bold text-white'>Send To</label>
                            <div className='d-flex gap-3 text-white'>
                                <Form.Check
                                    type='radio'
                                    id='send-all'
                                    name='sendMode'
                                    label='All Users'
                                    checked={sendMode === 'all'}
                                    onChange={() => {
                                        setSendMode('all')
                                        setSelectedUser(null)
                                    }}
                                />
                                <Form.Check
                                    type='radio'
                                    id='send-user'
                                    name='sendMode'
                                    label='Specific User'
                                    checked={sendMode === 'user'}
                                    onChange={() => setSendMode('user')}
                                />
                            </div>
                        </div>

                        {/* User Selection (shown when 'Specific User' is selected) */}
                        {sendMode === 'user' && (
                            <div className='mb-3'>
                                <label className='form-label fw-bold'>Select User</label>
                                <Select
                                    options={userOptions}
                                    value={selectedUser}
                                    onChange={setSelectedUser}
                                    isLoading={isLoadingUsers}
                                    placeholder='Search and select a user...'
                                    isClearable
                                    isSearchable
                                    classNamePrefix='react-select'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '45px',
                                            borderColor: '#dee2e6',
                                        }),
                                    }}
                                />
                            </div>
                        )}

                        {/* Title Input */}
                        <div className='mb-3'>
                            <ThemeInput
                                name="title"
                                control={control}
                                errors={errors}
                                rules={{
                                    required: {
                                        value: true,
                                        message: validationText.titleRequired
                                    }
                                }}
                                label="Title"
                                placeholder="Enter notification title"
                                type="text"
                            />
                        </div>

                        {/* Description Input */}
                        <div className='mb-3'>
                            <ThemeTextArea
                                name="description"
                                control={control}
                                errors={errors}
                                rules={{
                                    required: {
                                        value: true,
                                        message: validationText.descriptionRequired
                                    },
                                    maxLength: {
                                        value: validation.descriptionMax,
                                        message: validationText.descriptionMax
                                    },
                                }}
                                label="Message"
                                placeholder="Enter notification message..."
                                type="text"
                                maxLength={validation.descriptionMax}
                            />
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            className='btn-solid btn-purple w-100'
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size='sm' className='me-2' />
                                    Sending...
                                </>
                            ) : (
                                sendMode === 'all' ? 'Send to All Users' : 'Send to Selected User'
                            )}
                        </Button>
                    </Form>
                </Col>
            </Row>
        </div>
    )
}

export default SendPushNotifications